import {
  Box,
  H4,
  MaterialTableWrapper,
  MRT_TableOptions,
  MRT_ColumnDef,
  withThemeProvider,
} from '@ouster/webviz-react-components';
import type { MaterialTableWrapperProps } from '@ouster/webviz-react-components';

import { useMemo } from 'react';
import type { ProvisionedOrganization as TProvisionedOrganization } from 'src/reducers/adminDashboard';

import { formatDateLong } from '../utils';

export interface Organization {
  id: number;
  name: string;
  isProvisioned: boolean;
  members: number;
  recordingCount: number;
  createdAt: string; // When provisioning started
  activatedAt?: string; // When handover happened
  provisionerName?: string; // Name of FAE handling it
}

const ThemedMaterialTableWrapper =
  withThemeProvider<MaterialTableWrapperProps<TProvisionedOrganization>>(
    MaterialTableWrapper
  );

export default function ProvisionedOrganization({
  provisionedOrgs,
}: {
  provisionedOrgs: TProvisionedOrganization[];
}) {
  const columns = useMemo<MRT_ColumnDef<TProvisionedOrganization>[]>(
    () => [
      {
        accessorKey: 'organization_id',
        header: 'Organization ID',
      },
      {
        accessorKey: 'organization_name',
        header: 'Client Organization',
      },
      {
        accessorKey: 'provisioned_by_name',
        header: 'Provisioned By',
      },
      {
        accessorKey: 'provisioned_date',
        header: 'Invited Date',
        Cell: ({ cell }) => formatDateLong(cell.getValue<string>()),
      },
      {
        accessorKey: 'handed_over_at',
        header: 'Handover Date',
        Cell: ({ cell }) => formatDateLong(cell.getValue<string>()),
      },
      {
        accessorKey: 'member_count',
        header: 'Members',
      },
    ],
    []
  );

  const tableOptions: MRT_TableOptions<TProvisionedOrganization> = {
    columns,
    data: provisionedOrgs,
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      showGlobalFilter: true,
    },
    positionGlobalFilter: 'right',
    enableSorting: true,
    enableToolbarInternalActions: false,
    getRowId: (originalRow) => originalRow.organization_id.toString(),
    layoutMode: 'grid',
    muiTablePaperProps: {
      sx: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
    },
    muiTableContainerProps: {
      sx: {
        border: '1px solid',
        borderColor: 'rgba(255, 255, 255, 0.12)',
      },
    },
    muiTableBodyRowProps: {
      hover: false,
    },
  };

  if (provisionedOrgs.length === 0) {
    return null;
  }

  return (
    <Box position="relative">
      <H4
        color="gray.300"
        fontSize={14}
        position="absolute"
        left={0}
        top={4}
        zIndex={2}
        display="flex"
        alignItems="center"
        gap={2}
      >
        <Box
          borderRadius="full"
          w={2}
          h={2}
          bg="green.400"
          display="inline-block"
        />
        <span style={{ lineHeight: 1 }}>Provisioned Clients</span>
      </H4>
      <Box zIndex={1}>
        <ThemedMaterialTableWrapper {...tableOptions} />
      </Box>
    </Box>
  );
}
